import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { EMPTY_BOOTSTRAP, type Bootstrap, fetchBootstrap, mediaByKey } from '../api/bootstrap.js'
import fontManifest from './fontManifest.json'
import { AssetCache, downloadWithProgress } from './assetCache.js'
import { type FontManifestEntry, type LoadItem, buildLoadPlan } from './loadPlan.js'

/** 이 시간이 지나면 하객에게 탈출 버튼을 준다. 청첩장을 인질로 잡지 않는다. */
export const LOAD_TIMEOUT_MS = 8000

export type LoadPhase = 'loading' | 'ready'

export interface AssetLoaderState {
  phase: LoadPhase
  progress: number
  escapeAvailable: boolean
  bootstrap: Bootstrap
  cache: AssetCache
  /** 지금까지 도착한 blob URL들. 캐시가 바뀌면 이 객체가 새로 만들어져 리렌더를 부른다. */
  assets: Readonly<Record<string, string>>
  /** blob 다운로드가 아직 진행 중인 키들. */
  pending: ReadonlySet<string>
  enterAnyway: () => void
}

async function waitForFont(item: LoadItem): Promise<void> {
  // fonts.css가 이미 같은 URL을 요청한다. 여기서 다시 fetch→Blob→FontFace를 만들면
  // WebKit은 캐시를 공유하지 않아 모든 폰트를 두 번 전송한다. CSS가 선언한 face를
  // FontFaceSet으로 기다리면 요청은 한 번이고 로딩 화면은 실제 적용까지 유지된다.
  await document.fonts.load(`${item.weight ?? '400'} 1em "${item.family}"`)
}

/**
 * 갤러리 사진은 blob이 필요 없다 — `<img src>`가 매니페스트 URL을 그대로 쓰므로
 * 브라우저 HTTP 캐시만 데워 두면 스크롤이 닿는 순간 즉시 그려진다. 본문을 끝까지
 * 읽어야 캐시에 들어가지만, 다 읽은 뒤에는 아무것도 붙들지 않는다.
 */
async function warmHttpCache(url: string, signal: AbortSignal): Promise<void> {
  const response = await fetch(url, { signal })
  if (!response.ok) throw new Error(`prefetch http ${response.status} ${url}`)
  await response.arrayBuffer()
}

export function useAssetLoader(): AssetLoaderState {
  const cache = useMemo(() => new AssetCache(), [])
  const [phase, setPhase] = useState<LoadPhase>('loading')
  const [progress, setProgress] = useState(0)
  const [escapeAvailable, setEscape] = useState(false)
  const [bootstrap, setBootstrap] = useState<Bootstrap>(EMPTY_BOOTSTRAP)
  const [assets, setAssets] = useState<Record<string, string>>(() => cache.snapshot())
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set())
  const received = useRef(0)

  const enterAnyway = useCallback(() => setPhase('ready'), [])

  // 캐시는 React 밖의 가변 객체다. 구독하지 않으면 늦게 도착한 blob을 아무도
  // 보지 못한 채 화면이 스트리밍 소스에 머문다 — 이 구독이 유일한 발행 경로다.
  // 아래 다운로드 effect보다 먼저 선언해야 언마운트 시 구독 해제가 먼저 돈다.
  useEffect(() => cache.subscribe(() => setAssets(cache.snapshot())), [cache])

  useEffect(() => {
    // 폐기는 인스턴스가 아니라 마운트 단위다 — StrictMode의 개발 모드 재마운트가
    // 캐시를 영구히 죽이지 않도록 매 마운트마다 다시 연다. 수신 바이트도 같이
    // 되감지 않으면 진행률이 실제보다 앞서 달린다.
    cache.reopen()
    received.current = 0
    setProgress(0)

    const controller = new AbortController()
    const timer = setTimeout(() => setEscape(true), LOAD_TIMEOUT_MS)
    const inFlight = new Set<string>()
    const publishPending = () => setPending(new Set(inFlight))

    /** 자산 하나를 준비한다. 실패는 삼킨다 — 진입을 막지 않는다. */
    const takeItem = async (item: LoadItem, onBytes: (delta: number) => void) => {
      inFlight.add(item.id)
      publishPending()
      try {
        if (item.kind === 'font') {
          await waitForFont(item)
          // FontFaceSet에는 바이트 진행 콜백이 없으므로 적용이 끝난 시점에 선언된
          // 전체 크기를 반영한다.
          onBytes(item.bytes)
        } else {
          const blob = await downloadWithProgress(item.url, onBytes, controller.signal)
          cache.put(item.id, blob)
        }
      } catch {
        // 자산 하나가 실패해도 진입을 막지 않는다. 진행률은 그만큼 덜 찬 채 끝난다.
      } finally {
        inFlight.delete(item.id)
        publishPending()
      }
    }

    void (async () => {
      let data = EMPTY_BOOTSTRAP
      try {
        data = await fetchBootstrap(controller.signal)
      } catch {
        // 부트스트랩이 실패해도 진입시킨다. 미디어 없는 청첩장이 닫힌 청첩장보다 낫다.
      }
      setBootstrap(data)

      const plan = buildLoadPlan(data, fontManifest as FontManifestEntry[])

      // totalBytes는 critical(미디어+폰트) 바이트의 합일 뿐이다. 0이어도 deferred는
      // 여전히 존재할 수 있으므로(예: 결혼식 사진을 hero/main보다 먼저 올려둔 경우)
      // 여기서 return해 버리면 안 된다 — critical 구간만 건너뛰고 아래 deferred
      // 백그라운드 수신은 항상 실행되게 한다.
      if (plan.totalBytes === 0) {
        setProgress(1)
        setPhase('ready')
      } else {
        const bump = (delta: number) => {
          received.current += delta
          setProgress(Math.min(1, received.current / plan.totalBytes))
        }

        await Promise.all(plan.critical.map((item) => takeItem(item, bump)))
        setProgress(1)
        setPhase('ready')
      }

      // 진입한 뒤 조용히 이어받는다. 실패해도 무시한다.
      for (const item of plan.deferred) {
        if (controller.signal.aborted) return
        if (item.strategy === 'prefetch') {
          try {
            await warmHttpCache(item.url, controller.signal)
          } catch {
            /* 무시 */
          }
        } else {
          await takeItem(item, () => {})
        }
      }
    })()

    return () => {
      controller.abort()
      clearTimeout(timer)
      cache.clear()
    }
  }, [cache])

  return { phase, progress, escapeAvailable, bootstrap, cache, assets, pending, enterAnyway }
}

/**
 * 섹션에 걸어줄 소스를 고른다.
 *
 * blob이 있으면 blob, 아직 받는 중이면 아무것도 걸지 않는다 — 여기서 스트리밍
 * URL로 폴백하면 같은 파일을 두 번 받는다. 하객 수백 명이 같은 1분 안에 탈출
 * 버튼을 누르는 예식 당일 아침, 그 중복이 가정용 업링크를 그대로 두 배로 만든다.
 * 받을 계획이 없거나(용량 초과·변환 전) 실패한 뒤에야 스트리밍으로 붙는다.
 */
export function assetSrc(loader: AssetLoaderState, key: string): string | undefined {
  const blob = loader.assets[key]
  if (blob !== undefined) return blob
  if (loader.pending.has(key)) return undefined
  return mediaByKey(loader.bootstrap, key)?.url
}
